"use client";

import { useEffect, useRef } from "react";

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Replacement for browser confirm() — branded, consistent, themable, doesn't
// freeze the tab. Pair with useState to control open. Destructive variant uses
// red so the user can't miss the warning.
export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelBtnRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-maren-charcoal/60 backdrop-blur-sm px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className="w-full max-w-sm rounded-xl bg-white dark:bg-maren-charcoal border border-maren-sage/40 dark:border-maren-sage/20 shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="confirm-modal-title"
          className="text-base font-bold text-maren-charcoal dark:text-maren-stone mb-2"
        >
          {title}
        </h2>
        {message && (
          <p className="text-sm text-maren-charcoal/75 dark:text-maren-stone/80 mb-5 leading-relaxed">
            {message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            className="rounded-md border border-maren-sage/50 dark:border-maren-sage/30 bg-white dark:bg-maren-charcoal px-4 py-2 text-sm font-semibold text-maren-charcoal dark:text-maren-stone hover:bg-maren-stone/50 dark:hover:bg-maren-charcoal-elevated/60/70 transition"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-md px-4 py-2 text-sm font-bold text-white transition ${
              destructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-maren-green hover:bg-maren-green/90"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
